// @ts-nocheck
import React from 'react';

import { Box, Octicon, themeGet } from '@primer/react';
import capitalize from 'lodash/capitalize';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  LTButton,
  LTCounterLabel,
  LTPopover,
  LTTag,
  LTText,
  LTTestStatusLabel,
  LTLoader,
  LTTooltip,
} from '@lambdatestincprivate/lt-components';
import ClockIcon from '../../assets/clock.js';

import { truncateTextFromMiddle } from '../../utils/utilityFunctions.js';

import styles from './index.module.css';

const Dot = styled.span`
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${themeGet('colors.fg.disabled')};
`;

const TestCardWrapper = styled(Box)`
  display: flex;
  gap: ${themeGet('space.2')};
  padding: 12px 16px;
  border-bottom: 1px solid ${themeGet('colors.border.default')};
  cursor: pointer;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: #dddddd33;
  }
`;

const IconDescriptionText = styled(Box)`
  color: ${themeGet('colors.fg.default')};
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
`;

const InfoList = styled.ul`
  list-style-type: none;
  display: flex;
  align-items: center;
  gap: ${themeGet('space.2')};
  padding: 0px;
  margin: 0px;
  @media only screen and (max-width: 1024px) {
    flex-wrap: wrap;
  }
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: ${themeGet('space.2')};
`;

const ToolTipTag = ({ tags, totalCount }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: themeGet('space.2'),
        alignItems: 'start',
        flexDirection: 'column',
      }}
    >
      {tags.slice(2, totalCount).map((tag, index) => {
        const { name = '' } = tag || {};
        return (
          <LTTooltip text={name} key={`${name}-${index}`}>
            <LTTag text={name} />
          </LTTooltip>
        );
      })}
    </Box>
  );
};

const LTTestCard = ({
  accessibilityIcon: AccessibilityIcon = null,
  autoHealIcon: AutoHealIcon = null,
  testName = '',
  osIcon = '',
  browserIcon = '',
  flakyIcon: FlakyTestIcon = null,
  notesIcon = '',
  browser = '',
  timeTaken = '',
  testTimeSince = '',
  onLiveInteractionClick = () => {},
  reason = '',
  index = '',
  isFlaky = false,
  tags = [],
  isAccessibilityEnabled = false,
  isAutoHeal = false,
  showLiveInteraction = false,
  isSampleBuild = false,
  version = '',
  osVersion = '',
  osName = '',
  testStatus = '',
  testCardStyle = '',
  testStatusIcon = null,
}) => {
  const totalTags = Array.isArray(tags) ? Number(tags.length) : 0;
  const remainingTagCount = Array.isArray(tags) ? Number(totalTags) - 2 : 0;
  const truncatedTestName = truncateTextFromMiddle(testName, 90);
  return (
    <>
      <TestCardWrapper
        className={`${testCardStyle} ${
          index === 0 ? styles.firstTestItem : ''
        }`}
      >
        <Box aria-hidden="true">
          <LTTooltip
            direction={LTTooltip.Direction.RIGHT}
            text={capitalize(testStatus)}
          >
            {testStatusIcon ? (
              <span aria-hidden="true">
                <LTTestStatusLabel
                  iconSrc={testStatusIcon}
                  status={testStatus}
                  showStatusText={false}
                  width={16}
                  height={16}
                />
              </span>
            ) : (
              <LTLoader
                containerStyles={{ padding: 0 }}
                size={LTLoader.SpinnerSizes.SMALL}
                sx={{ color: '#0366D6', fill: '#0366D6', fillOpacity: '0.1' }}
              />
            )}
          </LTTooltip>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LTTooltip direction={LTTooltip.Direction.BOTTOM} text={testName}>
              <LTText
                aria-hidden="true"
                as="h4"
                sx={{
                  color: 'fg.default',
                  lineHeight: '20px',
                  margin: 0,
                  wordBreak: 'break-word',
                }}
                variant={LTText.TextVariant.SUBHEADER_BOLD}
              >
                {truncatedTestName}
              </LTText>
            </LTTooltip>
            {tags &&
              tags.length > 0 &&
              tags.slice(0, 2).map((tag) => {
                const { name = '' } = tag || {};
                return (
                  <LTTag
                    ariaHidden={true}
                    text={name}
                    toolTipDirection={LTTooltip.Direction.BOTTOM}
                  />
                );
              })}
            {remainingTagCount > 0 && (
              <LTPopover
                usePortal={true}
                ariaDescribedBy={`lt-test-card-popover-${index}`}
                text={<ToolTipTag totalCount={totalTags} tags={tags} />}
              >
                <LTCounterLabel
                  count={`+${remainingTagCount}`}
                  scheme={LTCounterLabel.Scheme.OUTLINED}
                />
              </LTPopover>
            )}
          </Box>
          {reason && (
            <Box
              aria-hidden="true"
              sx={{
                display: 'flex',
                alignItems: 'start',
                gap: '4px',
              }}
            >
              <img src={notesIcon} alt="" aria-hidden="true" style={{ marginTop: '3px' }} />
              <LTText
                variant={LTText.TextVariant.SMALL_REGULAR}
                sx={{
                  color: 'fg.muted',
                  wordBreak: 'break-word',
                  lineHeight: '20px',
                }}
              >
                {reason}
              </LTText>
            </Box>
          )}
          <InfoList
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {timeTaken && (
              <Item aria-hidden="true">
                <LTTooltip text="Total Time">
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <ClockIcon ariaLabel="Time Taken" />
                    <IconDescriptionText>{timeTaken}</IconDescriptionText>
                  </Box>
                </LTTooltip>
                <Dot />
              </Item>
            )}
            <Item aria-hidden="true">
              <LTTooltip text={capitalize(browser)}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <img
                    src={browserIcon}
                    alt={`${browser || version} browser`}
                    height="16"
                    width="16"
                  />
                  <IconDescriptionText sx={{ fontWeight: '600 !important' }}>
                    {version}
                  </IconDescriptionText>
                </Box>
              </LTTooltip>
              <Dot />
            </Item>
            <Item aria-hidden="true">
              <LTTooltip text={capitalize(osName)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <img
                    src={osIcon}
                    alt={`${osName || osVersion} os`}
                    height="16"
                    width="16"
                  />
                  <IconDescriptionText sx={{ fontWeight: '600 !important' }}>
                    {osVersion}
                  </IconDescriptionText>
                </Box>
              </LTTooltip>
              {isAccessibilityEnabled && (
                <Item aria-hidden="true">
                  <Dot />
                  <LTTooltip text="Accessibility Enabled">
                    <AccessibilityIcon height="16" width="16" />
                  </LTTooltip>
                </Item>
              )}
              {!isSampleBuild && (
                <>
                  <Dot />
                  <LTTooltip text="Time Since">
                    <LTText
                      variant={LTText.TextVariant.SMALL_REGULAR}
                      sx={{ color: 'fg.default' }}
                    >
                      {testTimeSince}
                    </LTText>
                  </LTTooltip>
                </>
              )}
            </Item>

            {isFlaky && (
              <Item aria-hidden="true">
                <Dot />
                <LTTooltip text="Flaky Test">
                  <FlakyTestIcon height="16" width="16" />
                </LTTooltip>
              </Item>
            )}
            {isAutoHeal && (
              <Item aria-hidden="true">
                <Dot />
                <LTTooltip text="Auto Healed">
                  <AutoHealIcon height="16" width="16" />
                </LTTooltip>
              </Item>
            )}
            {showLiveInteraction && (
              <Item>
                <Dot />
                <div className={`${styles.liveInteractionBtn}`}>
                  <LTButton
                    data-testid="test-card-live-btn"
                    id={`test-card-live-interaction-button-${index}`}
                    onClick={() => onLiveInteractionClick(osName)}
                    variant="default"
                  >
                    Live Interaction
                  </LTButton>
                </div>
              </Item>
            )}
          </InfoList>
        </Box>
      </TestCardWrapper>
    </>
  );
};

export default LTTestCard;
